import {useInfluencerQuery} from "@/api/influencerApi";
import BuildingIcon from "@/assets/colorable/building.svg?react";
import EditIcon from "@/assets/colorable/edit.svg?react";
import MergeArrowIcon from "@/assets/colorable/merge_arrow.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import TickIcon from "@/assets/colorable/tick_medium.svg?react";
import UsersIcon from "@/assets/colorable/users.svg?react";
import CompanyLogoPlaceholder from "@/assets/images/company_logo_placeholder.svg";
import UserAvatarPlaceholder from "@/assets/images/user_avatar_placeholder.svg";
import BankCardIcon from "@/assets/light_gradient/bank_card.svg?react";
import BuildingGradientIcon from "@/assets/light_gradient/building.svg?react";
import CircleWithQuestionMarkIcon from "@/assets/light_gradient/circle_with_question_mark.svg?react";
import SignOutIcon from "@/assets/light_gradient/sign_out.svg?react";
import UserIcon from "@/assets/light_gradient/user.svg?react";
import Button from "@/components/Common/Button";
import SidebarProfileTrigger from "@/components/NavBar/SidebarProfileTrigger";
import {useLogout} from "@/hooks/useLogout";
import {useHelpUrl} from "@/hooks/useNavigationHooks";
import {packages} from "@/hooks/usePackages";
import {usePaymentFailedInvoices} from "@/hooks/usePaymentFailedInvoices";
import {
  useIsAdmin,
  useIsCompany,
  useIsInfuencer,
  useIsSubbrand,
  useSelectedCompanyReactive,
  useUserPermission,
} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {getMissingProfileFields} from "@/store/company";
import {AGENCY_ROUTES_WITHOUT_NAVIGATION} from "@/store/constants";
import {
  isSidebarProfileMenuOpenAtom,
  paymentFailedModalDataAtom,
  selectedCompanyIdAtom,
  settingsModalAtom,
} from "@/store/jotaiAtoms";
import type {TSettingsModalState} from "@/store/types";
import useUserStore from "@/store/user";
import {Link, useLocation, useNavigate} from "@tanstack/react-router";
import {useSetAtom} from "jotai";
import findKey from "lodash/findKey";
import {Fragment, useEffect, useRef, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useClickAway, useHoverDirty, useMedia, useUpdateEffect} from "react-use";

const NavbarCompanyProfile: FC<{isCollapsed: boolean}> = ({isCollapsed}) => {
  const {t} = useTranslation(["navigation", "common", "multi_account"]);
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const helpUrl = useHelpUrl();
  const screenHeight = useMedia("(min-height: 868px)");
  const setSelectedCompanyId = useSetAtom(selectedCompanyIdAtom);
  const setSettingsModal = useSetAtom(settingsModalAtom);
  const setPaymentFailedModalData = useSetAtom(paymentFailedModalDataAtom);
  const setIsProfileMenuOpen = useSetAtom(isSidebarProfileMenuOpenAtom);
  const user = useUserStore(state => state.user);
  const userPermission = useUserPermission();
  const isAdmin = useIsAdmin();
  const isInfluencer = useIsInfuencer();
  const selectedCompany = useSelectedCompanyReactive();
  const {getPaymentFailedInvoices} = usePaymentFailedInvoices();
  const hasFailedPayment = (companyId?: number) =>
    !!companyId && getPaymentFailedInvoices({scope: {id: companyId, type: "brand"}}).length > 0;
  const hasIncompleteProfile = (companyId?: number) => {
    const company = user.sub_brands?.find(member => member.company.id === companyId)?.company;

    return !!company && getMissingProfileFields(company).length > 0;
  };
  const logout = useLogout();

  const {data} = useInfluencerQuery({
    id: Number(user.influencer_uuid),
  });
  const isCompany_ = useIsCompany();
  const isSubbrand = useIsSubbrand();
  const isCompany = isCompany_ || isSubbrand;
  const hasPlan = !!findKey(packages, {type: user.package_subscription?.name});

  const profileImage = isCompany ? (selectedCompany?.picture320 ?? selectedCompany?.picture) : data?.picture320;
  const displayName = isCompany ? selectedCompany?.company_name : user.username;

  const openSettings = (settings: TSettingsModalState) => {
    setIsDropdownOpen(false);
    setSettingsModal(settings);
  };
  const openUserProfile = () =>
    openSettings({scope: {id: selectedCompany?.id!, type: "brand"}, section: "account_profile"});

  const renderIndicator = ({label, tone}: {label: string; tone: "danger" | "warning"}) => (
    <>
      <span
        aria-hidden
        className={clsxm(
          "ml-auto size-2 shrink-0 self-center rounded-full border border-white",
          tone === "danger" ? "bg-danger-rubicund" : "bg-warning-mustard",
        )}
      />
      <span className="sr-only">{label}</span>
    </>
  );
  const renderFailedPaymentIndicator = (companyId?: number) =>
    hasFailedPayment(companyId) && renderIndicator({label: t("common:payment_failed"), tone: "danger"});

  const renderEditIndicator = (companyId?: number) =>
    hasFailedPayment(companyId)
      ? renderIndicator({label: t("common:payment_failed"), tone: "danger"})
      : hasIncompleteProfile(companyId) &&
        renderIndicator({label: t("navigation:profile_incomplete"), tone: "warning"});

  const multiAccountAdminButtons = isAdmin && (
    <>
      <Button
        onClick={() => navigate({to: "/admins/agency/create"})}
        buttonStyle="Blue"
        className="mx-3 mb-1.5 h-auto gap-2 px-4 text-sm">
        <BuildingIcon className="size-3.5" />
        {t("create_new_multi_account_user")}
      </Button>
      <Button
        onClick={() => navigate({to: "/admins/accounts/combine"})}
        buttonStyle="Blue"
        className="mx-3 mb-3 h-auto gap-2 px-4 text-sm">
        <MergeArrowIcon className="size-3.5" />
        {t("combine_accounts")}
      </Button>
    </>
  );
  const userPill = (
    <div className="flex flex-col">
      <button
        onClick={openUserProfile}
        className="border-neutral-grey-light-dark mx-3 mb-3 flex items-center justify-between gap-3 rounded-full border p-1 pr-2">
        <div className="flex items-center gap-3">
          <div className="border-neutral-grey-light-dark size-6 shrink-0 rounded-full border">
            <img src={user.profile_picture ?? UserAvatarPlaceholder} className="h-full w-full rounded-full" />
          </div>
          <span className="bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
            {user.first_name}
          </span>
        </div>
        <span className="text-neutral-black text-xs text-nowrap">
          {t(
            `multi_account:${(userPermission === "REFLUENCED_COMPANY_ADMIN" ? "ADMIN" : userPermission).toLowerCase() as "write"}_permissions`,
          )}
        </span>
      </button>
      {multiAccountAdminButtons}
    </div>
  );

  const hasHover = useMedia("(hover: hover)");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null!);

  useClickAway(dropdownRef, () => setIsDropdownOpen(false));
  useUpdateEffect(() => setIsDropdownOpen(false), [pathname]);

  const isHovered = useHoverDirty(dropdownRef);
  const isOpen = hasHover ? isHovered : isDropdownOpen;

  useEffect(() => {
    setIsProfileMenuOpen(isOpen);
    return () => setIsProfileMenuOpen(false);
  }, [isOpen, setIsProfileMenuOpen]);

  return (
    <div ref={dropdownRef} className="group relative">
      <SidebarProfileTrigger
        hasFailedPayment={hasFailedPayment(selectedCompany?.id)}
        hasIncompleteProfile={hasIncompleteProfile(selectedCompany?.id)}
        imageUrl={profileImage ?? (isCompany ? CompanyLogoPlaceholder : UserAvatarPlaceholder)}
        isCollapsed={isCollapsed}
        isOpen={isOpen}
        name={displayName}
        onClick={hasHover ? undefined : () => setIsDropdownOpen(!isDropdownOpen)}
        onFailedPaymentClick={() => {
          setIsDropdownOpen(false);
          setPaymentFailedModalData(getPaymentFailedInvoices({scope: {id: selectedCompany?.id!, type: "brand"}}));
        }}
      />
      <div
        aria-hidden
        className={clsxm(
          "bg-neutral-black/40 fixed inset-0 transition-opacity duration-200 sm:hidden",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={() => setIsDropdownOpen(false)}
      />
      <div
        className={clsxm(
          "absolute bottom-0 left-0 origin-bottom-left transition-all duration-200 sm:left-full sm:pb-2 sm:pl-2 md:bottom-(--cookie-banner-height)",
          hasHover
            ? "scale-0 opacity-0 delay-200 group-hover:scale-100 group-hover:opacity-100 group-hover:delay-0"
            : isDropdownOpen
              ? "scale-100 opacity-100"
              : "pointer-events-none scale-0 opacity-0",
        )}>
        <div
          className={clsxm(
            "border-neutral-white-dark flex max-h-[calc(100dvh-var(--cookie-banner-height)-1rem)] flex-col overflow-y-auto rounded-md border bg-white drop-shadow-lg",
            user.sub_brands?.length! > 1 ? "min-w-72" : "min-w-64",
          )}>
          <div className="border-neutral-grey-lightest flex flex-col border-b">
            {user.sub_brands?.length! > 1 ? (
              user.sub_brands?.map((subBrand, idx) => {
                const brand = subBrand.company;

                return (
                  <Fragment key={brand.id}>
                    {idx === 0 && (
                      <>
                        <div className="h-3" />
                        {userPill}
                      </>
                    )}
                    <div
                      key={brand.id}
                      className={clsxm(
                        "flex shrink flex-row justify-between gap-3 px-3 py-4",
                        selectedCompany?.id === brand.id && "bg-primary-powder",
                      )}>
                      <div className="flex grow flex-row items-center gap-3">
                        <img
                          src={brand.picture320 ?? brand.picture ?? CompanyLogoPlaceholder}
                          className={clsxm(
                            "border-neutral-grey-lightest size-10 self-start rounded-full border",
                            !screenHeight && "size-8",
                          )}
                        />
                        <div className={clsxm("flex grow flex-col", screenHeight ? "gap-3" : "gap-1")}>
                          <span className="font-medium">{brand.company_name}</span>
                          <div className={clsxm("mt-1 flex gap-2", !screenHeight ? "flex-row gap-4" : "flex-col")}>
                            <button
                              onClick={() => openSettings({scope: {id: brand.id, type: "brand"}, section: "users"})}
                              className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal transition-all hover:font-semibold max-sm:min-h-5 max-sm:py-1">
                              <UsersIcon className="size-3" />
                              {screenHeight && t("users")}
                            </button>
                            <button
                              onClick={() => openSettings({scope: {id: brand.id, type: "brand"}, section: "info"})}
                              className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal no-underline transition-all hover:font-semibold max-sm:min-h-5 max-sm:py-1">
                              <EditIcon className="size-3" />
                              {screenHeight && t("common:Edit")}
                              {renderEditIndicator(brand.id)}
                            </button>
                          </div>
                        </div>
                      </div>
                      {selectedCompany?.id === brand.id ? (
                        <TickIcon className="text-primary-medium-solid self-center" />
                      ) : (
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false);
                            setSelectedCompanyId(brand.id);

                            if (!AGENCY_ROUTES_WITHOUT_NAVIGATION.includes(pathname)) {
                              navigate({
                                to: "/company/$companyId/dashboard",
                                params: {companyId: brand.id},
                              });
                            }
                          }}
                          className="flex items-center justify-center self-center max-sm:size-11">
                          <RightArrowIcon className="border-primary-light-solid text-neutral-grey-light size-8 shrink-0 rounded-sm border p-2.5" />
                        </button>
                      )}
                    </div>
                  </Fragment>
                );
              })
            ) : (
              <>
                <div className="flex flex-col gap-2 px-2.5 py-3">
                  <div className="flex items-center gap-3 max-sm:min-h-12 max-sm:items-center">
                    <img
                      className="size-10 rounded-full object-cover"
                      src={profileImage ?? (isCompany ? CompanyLogoPlaceholder : UserAvatarPlaceholder)}
                      alt="Profile"
                      style={{borderStyle: "solid"}}
                    />
                    <div className="flex grow flex-col gap-0.5">
                      <span className="text-base font-medium">{displayName}</span>
                      {user.has_multi_account_users_access && (
                        <button
                          onClick={() =>
                            openSettings({scope: {id: selectedCompany?.id!, type: "brand"}, section: "users"})
                          }
                          className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal no-underline transition-all hover:font-semibold max-sm:min-h-5 max-sm:py-1">
                          <UsersIcon className="size-3" />
                          {t("users")}
                        </button>
                      )}
                      {isCompany ? (
                        <button
                          onClick={() =>
                            openSettings({scope: {id: selectedCompany?.id!, type: "brand"}, section: "info"})
                          }
                          className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal no-underline transition-all hover:font-semibold max-sm:min-h-5 max-sm:py-1">
                          <EditIcon className="size-3" />
                          {t("common:Edit")}
                          {renderEditIndicator(selectedCompany?.id)}
                        </button>
                      ) : (
                        <Link
                          to="/influencer/$influencerId/edit"
                          params={{influencerId: user.influencer_uuid!}}
                          className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal no-underline transition-all hover:font-semibold max-sm:min-h-5 max-sm:py-1">
                          <EditIcon className="size-3" />
                          {t("edit_account")}
                        </Link>
                      )}
                    </div>
                  </div>
                  {isCompany && !hasPlan && (
                    <Button
                      buttonStyle="Custom"
                      className="bg-primary-powder border-primary-light-solid text-primary-dark-solid hover:bg-primary-light-solid h-fit min-h-8 w-full rounded-full border text-base leading-none font-medium"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        navigate({to: "/plans"});
                      }}>
                      {t("join_now")}
                    </Button>
                  )}
                </div>
                {isInfluencer && multiAccountAdminButtons}
                {isCompany && userPill}
              </>
            )}
          </div>
          <Link
            className="hover:bg-neutral-white-dark border-neutral-grey-lightest flex h-auto cursor-pointer gap-2 border-b px-2.5 py-3 text-xs font-normal no-underline max-sm:min-h-12 max-sm:items-center"
            to={isCompany ? "/company/$companyId" : "/influencer/$influencerId"}
            params={{companyId: selectedCompany?.id!, influencerId: user.influencer_uuid!}}>
            {isCompany ? (
              <BuildingGradientIcon className="h-4 w-auto shrink-0" />
            ) : (
              <UserIcon className="h-4 w-auto shrink-0" />
            )}
            {t(isCompany ? "company_profile" : "your_profile")}
          </Link>
          {isCompany && (
            <div
              className="hover:bg-neutral-white-dark border-neutral-grey-lightest flex h-auto cursor-pointer gap-2 border-b px-2.5 py-3 text-xs font-normal max-sm:min-h-12 max-sm:items-center"
              onClick={openUserProfile}>
              <UserIcon className="h-4 w-auto shrink-0" />
              {t("your_profile")}
            </div>
          )}
          {isCompany && (
            <Can I="read" a="PaymentHistory">
              <Link
                className="hover:bg-neutral-white-dark border-neutral-grey-lightest flex h-auto cursor-pointer gap-2 border-b px-2.5 py-3 text-xs font-normal no-underline max-sm:min-h-12 max-sm:items-center"
                to="/company/$companyId/invoices"
                params={{companyId: selectedCompany?.id!}}>
                <BankCardIcon className="h-4 w-auto shrink-0" />
                {t("payment_history")}
                {renderFailedPaymentIndicator(selectedCompany?.id)}
              </Link>
            </Can>
          )}

          <a
            className="hover:bg-neutral-white-dark border-neutral-grey-lightest flex cursor-pointer gap-2 border-b px-2.5 py-3 no-underline max-sm:min-h-12 max-sm:items-center"
            href={helpUrl}>
            <CircleWithQuestionMarkIcon className="h-4 w-auto shrink-0" />
            <span className="text-xs font-normal">{t("help")}</span>
          </a>
          <div
            onClick={() => logout()}
            className="hover:bg-neutral-white-dark flex cursor-pointer gap-2 px-2.5 py-3 max-sm:min-h-12 max-sm:items-center">
            <SignOutIcon className="h-4 w-auto shrink-0" />
            <span className="text-xs font-normal">{t("logout")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavbarCompanyProfile;
